import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@components/atoms/Button';
import { Task } from '@models/task.model';
import { Activity } from '@models/activity.model';
import { Incident } from '@models/incident.model';
import { useActivity, useCreateNote, useIncident } from '@hooks/api';
import { VisuallyHiddenInput } from '@components/generic/VisuallyHiddenInput';
import { ProjectFeed } from '@pages/projects/components/ProjectFeed';
import AcknowledgeIncidentModal from '@components/AcknowledgeIncidentModal';
import { Input } from '@components/atoms/Input';
import { TextareaInput } from '@components/atoms/TextareaInput';
import { useTranslation } from '@hooks/useTranslation';

interface ActivityTabProps {
  taskId?: string;
  task?: Task | null;
}

export const ActivityTab: React.FC<ActivityTabProps> = ({ taskId, task }) => {
  const tTaskDetail = useTranslation('taskDetail');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<
    { file: File; preview: string | null; type: string }[]
  >([]);
  const [limit] = useState<number>(10);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [combinedActivities, setCombinedActivities] = useState<Activity[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(
    null,
  );

  const {
    activities,
    loading,
    page,
    pages,
    fetchActivitiesWithIncidents,
    loadMoreWithIncidents,
  } = useActivity();
  const { createNote, loading: createLoading } = useCreateNote();
  const { acknowledgeIncident, loading: incidentLoading } = useIncident();

  useEffect(() => {
    if (taskId && task?.projectId) {
      fetchActivitiesWithIncidents(taskId, task.projectId, 1, limit);
    }
  }, [taskId, task?.projectId, limit, fetchActivitiesWithIncidents]);

  useEffect(() => {
    setCombinedActivities(activities);
  }, [activities]);

  // Reset states when activities change
  useEffect(() => {
    setIsLoadingMore(false);
  }, [activities]);

  const handleAcknowledgeConfirm = async () => {
    if (selectedIncident) {
      const result = await acknowledgeIncident(selectedIncident.incidentId);
      if (result) {
        setModalOpen(false);
        setSelectedIncident(null);
        // Refresh activities
        if (taskId && task?.projectId) {
          fetchActivitiesWithIncidents(taskId, task.projectId, 1, limit);
        }
      }
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedIncident(null);
  };

  const handleLoadMore = useCallback(async () => {
    if (taskId && task?.projectId && page < pages && !isLoadingMore) {
      setIsLoadingMore(true);
      try {
        await loadMoreWithIncidents(taskId, task.projectId, limit);
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [
    taskId,
    task?.projectId,
    page,
    pages,
    isLoadingMore,
    loadMoreWithIncidents,
    limit,
  ]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0) {
      const newFiles = [...files, ...selectedFiles];
      setFiles(newFiles);

      // Create previews for new files
      const newPreviews = selectedFiles.map((file) => {
        const fileType = file.type.startsWith('image/') ? 'image' : 'document';
        if (fileType === 'image') {
          const reader = new FileReader();
          return new Promise<{
            file: File;
            preview: string | null;
            type: string;
          }>((resolve) => {
            reader.onload = (e) => {
              resolve({
                file,
                preview: e.target?.result as string,
                type: fileType,
              });
            };
            reader.readAsDataURL(file);
          });
        } else {
          return Promise.resolve({ file, preview: null, type: fileType });
        }
      });

      Promise.all(newPreviews).then((previews) => {
        setFilePreviews((prev) => [...prev, ...previews]);
      });
    }
    event.target.value = '';
  };

  const handleAddNote = async () => {
    if (!title.trim() && !description.trim() && files.length === 0) {
      return false;
    }

    try {
      const noteData = {
        name: title.trim() || tTaskDetail.activityTab.defaultNoteName,
        description: description.trim() || undefined,
        taskId: taskId || undefined,
        projectId: task?.projectId || undefined,
      };

      const success = await createNote(
        noteData,
        files.length > 0 ? files : undefined,
      );

      if (success) {
        setTitle('');
        setDescription('');
        setFiles([]);
        setFilePreviews([]);
        if (taskId && task?.projectId) {
          fetchActivitiesWithIncidents(taskId, task.projectId, 1, limit);
        }
      }
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  return (
    // Fill the modal body's visible height so the panel itself doesn't scroll:
    // the Add Activity form stays pinned at top and only the activity list scrolls
    // (invisible scrollbar). The 230px offset accounts for the modal header + tabs
    // + footer above/below this tab.
    <div className="space-y-4 flex flex-col h-[calc(100vh-230px)] min-h-0">
      {/* Add Activity Form */}
      <div className="bg-white rounded-lg flex-shrink-0">
        <p className="text-sm font-medium text-gray-700 mb-3">{tTaskDetail.activityTab.addActivity}</p>

        {filePreviews.length > 0 && (
          <div className="mb-3">
            <p className="text-sm text-gray-600 mb-2">
              {tTaskDetail.activityTab.selectedFiles.replace('{n}', String(filePreviews.length))}
            </p>
            <div className="flex flex-wrap gap-2">
              {filePreviews.map((filePreview, index) => (
                <div key={index} className="relative inline-block">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-200 group border">
                    {filePreview.type === 'image' && filePreview.preview ? (
                      <img
                        src={filePreview.preview}
                        alt={filePreview.file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <svg
                          className="w-6 h-6"
                          fill="currentColor"
                          viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        const newFiles = files.filter((_, i) => i !== index);
                        const newPreviews = filePreviews.filter(
                          (_, i) => i !== index,
                        );
                        setFiles(newFiles);
                        setFilePreviews(newPreviews);
                      }}
                      className="absolute inset-0 bg-white bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer"
                      title={tTaskDetail.activityTab.removeFile}>
                      <div className="rounded-full flex items-center justify-center text-gray-500">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </button>
                  </div>
                  <p
                    className="text-xs text-gray-500 mt-1 truncate w-24"
                    title={filePreview.file.name}>
                    {filePreview.file.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Input
            type="text"
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={tTaskDetail.activityTab.noteTitlePlaceholder}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextareaInput
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
            placeholder={tTaskDetail.activityTab.descriptionPlaceholder}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                variant="tertiary"
                type="button"
                className="text-blue-600 border-blue-600 hover:bg-blue-50">
                <label className="cursor-pointer w-full h-full flex items-center justify-center">
                  <VisuallyHiddenInput
                    type="file"
                    accept="*/*"
                    multiple
                    onChange={handleFileChange}
                  />
                  {tTaskDetail.activityTab.attachFiles}
                </label>
              </Button>
            </div>

            <Button
              variant="primary"
              onClick={handleAddNote}
              disabled={
                createLoading ||
                (!title.trim() && !description.trim() && files.length === 0)
              }
              loading={createLoading}>
              {tTaskDetail.activityTab.addNote}
            </Button>
          </div>
        </div>
      </div>

      {/* Activities List — the only part that scrolls (invisible scrollbar) */}
      <div className="bg-white rounded-lg flex-1 min-h-0 overflow-y-auto no-scrollbar">
        {loading && combinedActivities.length === 0 ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : combinedActivities.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>{tTaskDetail.activityTab.noActivitiesYet}</p>
          </div>
        ) : (
          // No inner scroll box: let the modal body be the single scroll area so
          // the activity list isn't a cramped 400px box with its own scrollbar
          // nested inside the modal's scroll (that double-scroll was the bad UX).
          <ProjectFeed
            activities={combinedActivities}
            paginationActions={{
              loadMore: handleLoadMore,
              hasMore: page < pages,
              isLoading: isLoadingMore,
              isFetchingMore: isLoadingMore,
            }}
          />
        )}
      </div>

      {/* Acknowledge Incident Modal */}
      <AcknowledgeIncidentModal
        open={modalOpen}
        onClose={handleModalClose}
        onConfirm={handleAcknowledgeConfirm}
        incident={selectedIncident}
        isAcknowledging={incidentLoading}
      />
    </div>
  );
};
